import {setTitle} from '@github-ui/document-metadata'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {Link, useParams} from '@github-ui/react-router'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import {useEffect} from 'react'

import {useRepoIssuesSidebarPanes} from '../../components/sidebar/use-repo-issues-sidebar-panes'
import {LABELS} from '../../constants/labels'
import {IssueViewsPage} from '../../lazy-routes'
import {savedViewQueryOptions} from '../../queries/saved-views'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import styles from './RepositorySavedViewPage.module.css'

function RepositorySavedViewContent() {
  const {owner = '', repo = '', id = ''} = useParams<{owner: string; repo: string; id: string}>()
  const viewsPath = `/${owner}/${repo}/issues/views`

  const {data, isError, isLoading} = useQuery(savedViewQueryOptions({owner, repo, id}))

  useEffect(() => {
    if (data?.savedView?.name) {
      setTitle(LABELS.documentTitleForView(data.savedView.name))
    }
  }, [data?.savedView?.name])

  return (
    <div aria-busy={isLoading} data-hpc>
      <Link to={viewsPath} className={styles.backLink}>
        <ArrowLeftIcon />
        Back to Views
      </Link>

      {isError ? (
        <p role="alert">This view doesn’t exist or has been deleted.</p>
      ) : data?.savedView ? (
        <>
          <Heading as="h1" variant="medium">
            {data.savedView.name}
          </Heading>
          {data.savedView.description && <p>{data.savedView.description}</p>}
        </>
      ) : null}
    </div>
  )
}

export function RepositorySavedViewPage() {
  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueViewsPage.preload()
    })
  }, [])

  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- See above; issue_pinned_views_index_page is similarly actor-aware.
  const indexPageEnabled = useFeatureFlag('issue_pinned_views_index_page')
  const sidebarPanes = useRepoIssuesSidebarPanes()
  if (!pinnedViewsEnabled || !indexPageEnabled) {
    return null
  }

  return (
    <AnalyticsWrapper category="Repository Saved View">
      <ThreePanesLayout
        contentAs="div"
        resizeable={false}
        leftPaneWidth="medium"
        leftPane={sidebarPanes.leftPane}
        collapsibleLeftPane={sidebarPanes.collapsibleLeftPane}
        middlePane={<RepositorySavedViewContent />}
      />
    </AnalyticsWrapper>
  )
}
